import {
  trigger, state, style, transition,
  animate, group, query, stagger, keyframes
} from '@angular/animations';

export const SlideDirectionAnimation = trigger('SlideDirection', [
  state('left', style({ transform: 'translateX(0%)', opacity: '1', visibility: 'visible' })),
  state('up', style({ transform: 'translateY(0%)', opacity: '1', visibility: 'visible' })),
  // Enter transitions
  transition('void => left', [
    style({ opacity: '0', visibility: 'hidden', transform: 'translateX(100%)' }),
    animate('600ms ease-in', style({ opacity: '1', visibility: 'visible', transform: 'translateX(0%)' }))
  ]),
  transition('void => up', [
    style({ opacity: 0, visibility: 'hidden', transform: 'translateY(100%)' }),
    animate('400ms ease-in-out', style({ opacity: '1', visibility: 'visible', transform: 'translateY(0%)' }))
  ]),
  // Leave transitions
  transition('left => void', [
    animate('600ms ease-in', style({ opacity: '0', visibility: 'hidden', transform: 'translateX(100%)' }))
  ]),
  transition('up => void', [
    animate('400ms ease-in-out', style({ opacity: '0', visibility: 'hidden', transform: 'translateY(100%)' }))
  ])
]);

export const SlideInOutAnimation = [
  trigger('slideInOut', [
    transition(':enter', [
      style({ opacity: '0', visibility: 'hidden', transform: 'translateX(100%)' }),
      animate('600ms ease-in', style({ opacity: '1', visibility: 'visible', transform: 'translateX(0%)' }))
    ]),
    transition(':leave', [
      animate('600ms ease-in', style({ opacity: '0', visibility: 'hidden', transform: 'translateX(100%)' }))
    ])
  ])
];

export const SlideLeftToRightAnimation = [
  trigger('slideRight', [
    transition(':enter', [
      style({ opacity: '0', visibility: 'hidden', transform: 'translateX(-100%)' }),
      animate('600ms ease-in', style({ opacity: '1', visibility: 'visible', transform: 'translateX(0%)' }))
    ]),
    transition(':leave', [
      animate('600ms ease-in', style({ opacity: '0', visibility: 'hidden', transform: 'translateX(-100%)' }))
    ])
  ])
];

export const FadeAnimation = [
  trigger('fade', [
    transition('void => *', [
      style({ opacity: 0 }),
      animate(2000, style({ opacity: 1 }))
    ])
  ])
];

export const SlideUpDownAnimation = [
  trigger('slideUpDown', [
    state('in', style({ height: '*', opacity: 0 })),
    transition(':leave', [
      style({ height: '*', opacity: 1 }),
      group([
        animate(300, style({ height: 0 })),
        animate('200ms ease-in-out', style({ opacity: '0' }))
      ])
    ]),
    transition(':enter', [
      style({ height: '0', opacity: 0 }),
      group([
        animate(300, style({ height: '*' })),
        animate('400ms ease-in-out', style({ opacity: '1' }))
      ])
    ])
  ])
];


export const SlideUpAnimation = [
  trigger('slideUp', [
    // Transition for entering the "in" state from any state, including "off"
    transition('* => in', [
      style({ opacity: '0', visibility: 'hidden', transform: 'translateY({{startY}})' }),
      animate('{{duration}} ease-in', style({ opacity: '1', visibility: 'visible', transform: 'translateY(0%)' }))
    ], { params: { startY: '100%', duration: '600ms' } }),

    // Transition for leaving the "in" state to the "off" state
    transition('in => off', [
      style({ opacity: '1', visibility: 'visible', transform: 'translateY(0%)' }),
      animate('{{duration}} ease-in', style({ opacity: '0', visibility: 'hidden', transform: 'translateY({{endY}})' }))
    ], { params: { endY: '100%', duration: '600ms' } }),

    // Ensure the "off" state does nothing
    transition('off => off', [])
  ])
];

export const SlideDownAnimation = [
  trigger('slideDown', [
    transition(':enter', [
      style({ opacity: '0', visibility: 'hidden', transform: 'translateY(-100%)' }),
      animate('600ms ease-in', style({ opacity: '1', visibility: 'visible', transform: 'translateY(0%)' }))
    ]),
    transition(':leave', [
      style({ opacity: '1', visibility: 'visible', transform: 'translateY(0%)' }),
      animate('600ms ease-in', style({ opacity: '0', visibility: 'hidden', transform: 'translateY(-100%)' }))
    ])
  ])
];

// [@slide]="{value: '', params: { speed: '300ms', transformType: 'translateY', transformDefaultValue: '(0%)', transformEnterValue: '(-50%)', transformLeaveValue: '(-50%)'}}" OR [@fadeInDelay]
export const SlideAnimation = [
  trigger('slide', [
    transition(':enter', [
      style({ opacity: '0', visibility: 'hidden', transform: '{{transformType}}{{transformEnterValue}}' }),
      animate('{{speed}} ease-in', style({ opacity: '1', visibility: 'visible', transform: '{{transformType}}{{transformDefaultValue}}' }))
    ], { params: { speed: '300ms', transformType: 'translateY', transformDefaultValue: '(0%)', transformEnterValue: '(-50%)' } }),
    transition(':leave', [
      style({ opacity: '1', visibility: 'visible', transform: '{{transformType}}{{transformDefaultValue}}' }),
      animate('{{speed}} ease-in', style({ opacity: '0', visibility: 'hidden', transform: '{{transformType}}{{transformLeaveValue}}' }))
    ], { params: { speed: '300ms', transformType: 'translateY', transformDefaultValue: '(0%)', transformLeaveValue: '(-50%)' } })
  ])
];

export const FadeInAnimation = [
  trigger('fadeIn', [
    transition('void => *', [
      style({ opacity: 0 }),
      animate('{{time}}ms', style({ opacity: 1 }))
    ],
      { params: { time: 500 } }
    )
  ])
];

// [@fadeInDelay]="{value: '', params: { delay: 1000}}" OR [@fadeInDelay]
export const FadeInDelay = [
  trigger('fadeInDelay', [
    transition('void => *', [
      style({ opacity: '0' }),
      animate('800ms {{delay}}ms ease-in', style({ opacity: 1 }))
    ],
      { params: { delay: 500 } } //Fallback value; else it could crash when no delay is passed
    )
  ])
];

export const ListAnimation = [
  trigger('listAnimation', [
    transition('* => *', [
      query(':leave', [
        stagger(100, [
          animate('0.5s', style({ opacity: 0 }))
        ])
      ], { optional: true }),
      query(':enter', [
        style({ opacity: 0 }),
        stagger(100, [
          animate('0.5s', style({ opacity: 1 }))
        ])
      ], { optional: true })
    ])
  ])
];

export const ValueChangeAnimation = [
  trigger('valueChange', [
    transition(':enter', [
      style({ opacity: '0', visibility: 'hidden', transform: 'translateY(100%)' }),
      animate('600ms ease-in', style({ opacity: '1', visibility: 'visible', transform: 'translateY(0%)' }))
    ]),
    transition(':leave', [
      animate('600ms ease-in', style({ opacity: '0', visibility: 'hidden', transform: 'translateY(100%)' }))
    ])
  ])
];


export const StaggerAnimation = [
  trigger('stagger', [
    transition('* => *', [
      query(':enter', [
        style({ opacity: 0 }),
        stagger(500, [animate('0.2s', style({ opacity: 1 }))])
      ], { optional: true }
      )
    ])
  ])
];

export const GrowShirnkAnimation = [
  trigger('bubbleGrow', [
    transition(':enter', [
      animate('1000ms ease-in-out', keyframes([
        style({ transform: 'scale(0)' }),
        style({ transform: 'scale(1.1)' }),
        style({ transform: 'scale(1)' }),
      ]))
    ]),
    transition(':leave', [
      animate('1000ms ease-in-out', keyframes([
        style({ transform: 'scale(1)' }),
        style({ transform: 'scale(1.1)' }),
        style({ transform: 'scale(0)' })
      ]))
    ]),
  ])
];

export const ZoomOutAnimation = [
  trigger('zoomOut', [
    transition(':enter', [
      animate('1000ms ease-in-out', keyframes([
        style({ opacity: '0', visibility: 'hidden', transform: 'scale(8)' }),
        style({ opacity: '1', visibility: 'visible', transform: 'scale(1)' }),
      ]))
    ]),
    transition(':leave', [
      animate('1000ms ease-in-out', keyframes([
        style({ opacity: '1', visibility: 'visible', transform: 'scale(1)' }),
        style({ opacity: '0', visibility: 'hidden', transform: 'scale(8)' }),
      ]))
    ]),
  ])
];

export const ZoomInAnimation = [
  trigger('zoomIn', [
    transition(':enter', [
      animate('1000ms ease-in-out', keyframes([
        style({ opacity: '0', visibility: 'hidden', transform: 'scale(0)' }),
        style({ opacity: '1', visibility: 'visible', transform: 'scale(1)' }),
      ]))
    ]),
    transition(':leave', [
      animate('1000ms ease-in-out', keyframes([
        style({ opacity: '1', visibility: 'visible', transform: 'scale(1)' }),
        style({ opacity: '0', visibility: 'hidden', transform: 'scale(0)' }),
      ]))
    ]),
  ])
];


export const animationsCollection = [
  trigger("myAnimation", [
    transition('void => fade', [
      style({ opacity: 0 }),
      animate(2000, style({ opacity: 1 }))
    ]),
    transition('void => slideUp', [
      style({ opacity: '0', visibility: 'hidden', transform: 'translateY(100%)' }),
      animate('600ms ease-in', style({ opacity: '1', visibility: 'visible', transform: 'translateY(0%)' }))
    ]),

    // transition('void => slideUp', [
    //   query(':leave', [
    //     animate('1000ms ease-in', style({ opacity: '0', visibility: 'hidden', transform: 'translateY(100%)' }))
    //   ], { optional: true }),
    //   query(':enter', [
    //     style({ opacity: '0', visibility: 'hidden', transform: 'translateY(100%)' }),
    //     animate('1000ms ease-in', style({ opacity: '1', visibility: 'visible', transform: 'translateY(0%)' }))
    //   ], { optional: true })
    // ])
  ])
]


// export const flyAnimations = [
//   trigger('fly', [
//     transition('* => left', [
//       query(':enter', [
//         style({ opacity: '0', visibility: 'hidden', transform: 'translateY(100%)' }),
//         animate('600ms ease-in', style({ opacity: '1', visibility: 'visible', transform: 'translateY(0%)' }))
//       ], { optional: true }),
//       query(':leave', [
//         animate('600ms ease-in', style({ opacity: '0', visibility: 'hidden', transform: 'translateY(100%)' }))
//       ], { optional: true })
//     ])
//   ]),
// ]

export const flyAnimations = [
  trigger('fly', [
    transition('void => left', [
      style({ opacity: '0', visibility: 'hidden', transform: 'translateX(100%)' }),
      animate('600ms ease-in', style({ opacity: '1', visibility: 'visible', transform: 'translateX(0%)' }))
    ]),
    transition('void => bottom', [
      style({ opacity: '0', visibility: 'hidden', transform: 'translateY(100%)' }),
      animate('600ms ease-in', style({ opacity: '1', visibility: 'visible', transform: 'translateY(0%)' }))
    ]),
  ]),
]


export const RotatedState = [
  trigger('rotatedState', [
    state('up', style({ transform: 'rotate(0)' })),
    state('down', style({ transform: 'rotate(-180deg)' })),
    transition('down => up', animate('100ms ease-out')),
    transition('up => down', animate('100ms ease-in'))
  ])
]

 export const slideInFromBottom = trigger('routeAnimations', [
  transition('* => GalleryPage', [
    style({ position: 'relative' }),
    query(':enter, :leave', [
      style({
        position: 'absolute',
        width: '100%',
        top: 0,
        left: 0,
      }),
    ], { optional: true }),

    query(':enter', [
      style({ transform: 'translateY(100%)', opacity: 0 }),
    ], { optional: true }),

    group([
      query(':leave', [
        animate('300ms ease', style({ opacity: 0 })),
      ], { optional: true }),

      query(':enter', [
        animate('300ms ease', style({ transform: 'translateY(0)', opacity: 1 })),
      ], { optional: true }),
    ]),
  ]),
]);
